import { useCallback, KeyboardEvent, ChangeEvent } from 'react';

import KEY_CODES from '../../../../constants/KEY_CODES';
import { DropListComposedProps, DropListOption } from '../DropList';

interface UseDropListKeyboardNavigationOptions<Value, OptionValue> {
  options: DropListComposedProps<Value, OptionValue>['options'];
  focusedActionIndex: number;
  isOpened: boolean;
  lastClosedValue: Value | string;
  internalValue: Value | string;
  inputText: string;
  setInputText: (input: string) => void;
  homeEndNavigation: boolean;
  setIsOpened: (isOpened: boolean) => void;
  setFocusedActionIndex: (index: number) => void;
  handleValueChange: (value: OptionValue | Value | string, event: ChangeEvent | { type: 'click' | 'change' | 'keydown'; selectedOption?: DropListOption<OptionValue> }, options?: {}) => void;
  shouldStayOpened: DropListComposedProps<Value, OptionValue>['shouldStayOpened'];
  onKeyDown: (event: KeyboardEvent<any>) => void;
  onOptionFocused: (value: OptionValue | string) => void;

}

const useDropListKeyboardNavigation = <Value, OptionValue>({
  options, focusedActionIndex, setFocusedActionIndex, handleValueChange, setIsOpened, isOpened, lastClosedValue, setInputText, inputText, homeEndNavigation, internalValue, shouldStayOpened, onKeyDown, onOptionFocused,
}: UseDropListKeyboardNavigationOptions<Value, OptionValue>) => {
  const normalizeIndex = useCallback((index: number) => {
    if (index < 0) {
      return 0;
    }

    if (index > options.length - 1) {
      return options.length - 1;
    }

    return index;
  }, [options.length]);

  const checkIsKeyInput = useCallback((event: KeyboardEvent) => (
    event.key.length === 1
  ), []);

  const selectItemByIndex = useCallback((event: KeyboardEvent, index: number) => {
    event.preventDefault();

    const normalizedIndex = normalizeIndex(index);

    if (normalizedIndex !== focusedActionIndex) {
      setFocusedActionIndex(normalizedIndex);
      if (onOptionFocused) {
        onOptionFocused(options[normalizedIndex].value);
      } else {
        handleValueChange(options[normalizedIndex].value, { type: 'keydown' });
      }
    }
  }, [normalizeIndex, options, focusedActionIndex, handleValueChange, setFocusedActionIndex, onOptionFocused]);

  // eslint-disable-next-line consistent-return
  const handleKeyboardNavigation = useCallback((event: KeyboardEvent) => {
    if (event.altKey && (event.keyCode === KEY_CODES.UP || event.keyCode === KEY_CODES.DOWN)) {
      setIsOpened(!isOpened);
    } else if (event.keyCode === KEY_CODES.ENTER || event.keyCode === KEY_CODES.SPACE) {
      if (!isOpened || (!shouldStayOpened(internalValue, event, { isOpened, focusedActionIndex }))) {
        setIsOpened(!isOpened);
      }
    } else if (event.keyCode === KEY_CODES.UP) {
      selectItemByIndex(event, focusedActionIndex - 1);
    } else if (event.keyCode === KEY_CODES.DOWN) {
      selectItemByIndex(event, focusedActionIndex + 1);
    } else if ((homeEndNavigation && event.keyCode === KEY_CODES.HOME) || event.keyCode === KEY_CODES.PAGE_UP) {
      selectItemByIndex(event, 0);
    } else if ((homeEndNavigation && event.keyCode === KEY_CODES.END) || event.keyCode === KEY_CODES.PAGE_DOWN) {
      selectItemByIndex(event, options.length - 1);
    } else if (event.keyCode === KEY_CODES.ESC) {
      handleValueChange(lastClosedValue, { type: 'keydown' });
      // To prevent the parent modal from closing.
      event.stopPropagation();
      setIsOpened(false);
    } else if (checkIsKeyInput(event)) {
      setInputText(`${inputText}${event.key}`.toLowerCase());
    }

    if (onKeyDown) onKeyDown(event);
  }, [homeEndNavigation, checkIsKeyInput, onKeyDown, setIsOpened, isOpened, shouldStayOpened, internalValue, selectItemByIndex, focusedActionIndex, options.length, handleValueChange, lastClosedValue, setInputText, inputText]);

  return {
    handleKeyboardNavigation,
  };
};

export default useDropListKeyboardNavigation;
